import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { AutoAwesome as SparklesIcon } from "@mui/icons-material";
import { useWatch } from "react-hook-form";
import { useMemo } from "react";
import Button from "../../../components/Buttons/Button";
import Select from "../../../components/Form/Select";
import SelectContentLanguage from "../../../components/Form/SelectContentLanguage";
import TextField from "../../../components/Form/TextField";
import { useTranslationApp } from "../../../lib/i18next";
import { useSelectorApp } from "../../../lib/redux";
import {
  CONTENT_LANGUAGE_PRESET,
  getContentLanguageOptions,
} from "../../../utils/catalogs/contentLanguage";
import {
  BUTTON_EDIT,
  BUTTON_GENERATE,
  PRICING_CREDIT_USAGE_CREDIT,
  PRICING_CREDIT_USAGE_CREDITS,
  TEMPLATES_PRODUCE_CHANGE,
  TEMPLATES_PRODUCE_CONFIG_KICKER,
  TEMPLATES_PRODUCE_DIMENSIONS,
  TEMPLATES_PRODUCE_EMPTY_TEMPLATES,
  TEMPLATES_PRODUCE_GENERATE,
  TEMPLATES_PRODUCE_GENERATE_SINGLE,
  TEMPLATES_PRODUCE_LANGUAGE,
  TEMPLATES_PRODUCE_ROW_COUNT,
  TEMPLATES_PRODUCE_ROW_COUNT_SINGLE,
  TEMPLATES_PRODUCE_SELECTED_TEMPLATE_LABEL,
  TEMPLATES_PRODUCE_SUMMARY,
  TEMPLATES_PRODUCE_SUMMARY_COST,
  TEMPLATES_PRODUCE_SUMMARY_COST_LABEL,
  TEMPLATES_PRODUCE_SUMMARY_FORMAT,
  TEMPLATES_PRODUCE_SUMMARY_IMAGES_COUNT,
  TEMPLATES_PRODUCE_SUMMARY_IMAGES_COUNT_SINGLE,
  TEMPLATES_PRODUCE_SUMMARY_RATE,
  TEMPLATES_PRODUCE_SUMMARY_SIZE_COUNT,
  TEMPLATES_PRODUCE_SUMMARY_SIZES_COUNT,
  TEMPLATES_PRODUCE_SUMMARY_SLIDE_COUNT,
  TEMPLATES_PRODUCE_SUMMARY_SLIDES_COUNT,
  TEMPLATES_PRODUCE_SUMMARY_VARIATION_LABEL,
  TEMPLATES_PRODUCE_SUMMARY_VARIATIONS_LABEL,
  TEMPLATES_PRODUCE_TEMPLATE,
  TEMPLATES_PRODUCE_VARIATIONS,
  TEMPLATES_PRODUCE_VARIATIONS_PER_ROW,
  TEMPLATES_PRODUCE_IMAGES_PROCESSING,
  TEMPLATES_PRODUCE_IMAGES_UNUSABLE,
} from "../../../i18n/keysTranslations";
import { resolveActionCostForPlanType } from "../../../utils/creditActionCosts";
import { useCreditActionCosts } from "../../../hooks/useCreditActionCosts";
import ProduceToggleChips from "./ProduceToggleChips";
import TemplateLayoutPreview from "../editor/TemplateLayoutPreview";
import {
  clampProduceVariationCount,
  getProduceProductionRowCount,
  PRODUCE_MAX_ROWS,
  PRODUCE_MIN_VARIATION_COUNT,
  sanitizeProduceVariationCountInput,
} from "./produceRows";
import {
  produceCardSx,
  produceChangeButtonSx,
  produceCardTitleSx,
  produceFieldLabelSx,
  produceKickerSx,
  produceSectionDividerSx,
  produceSelectSx,
  produceTemplateSelectSx,
  produceSelectedTemplateBoxSx,
  produceSummaryBoxSx,
  produceSummaryCostSx,
  produceSummaryKickerSx,
  produceSummaryRowSx,
  produceSummaryRowsSx,
  produceSummaryTotalSx,
} from "./produceCardStyles";

const optionalRules = { required: false };
const SUMMARY_PART_SEPARATOR = " · ";
const SUMMARY_FORMULA_SEPARATOR = " × ";

const templateSelectMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 400,
      width: 320,
    },
  },
};

const joinSummaryParts = (parts = [], separator = SUMMARY_PART_SEPARATOR) =>
  parts.filter(Boolean).join(separator);

const countLabel = ({ t, count, singularKey, pluralKey } = {}) =>
  t(Number(count) === 1 ? singularKey : pluralKey, { count });

const getProduceCostUnit = ({ t, count }) =>
  t(
    Number(count) === 1
      ? PRICING_CREDIT_USAGE_CREDIT
      : PRICING_CREDIT_USAGE_CREDITS
  );

const ProduceSummaryRow = ({ label, value, hint }) => (
  <Box sx={produceSummaryRowSx}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ textAlign: "right", minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {hint}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

const ProduceTemplateCard = ({
  control,
  selectedCard = null,
  hasProduceableCards = false,
  showTemplateSelect = false,
  templateOptions = [],
  templateName = "",
  showFormatToggles = false,
  formatOptions = [],
  selectedArtboardIDs = [],
  showVariationCount = false,
  hasVariables = false,
  setValue = () => {},
  rowCount = 0,
  variationCount = 0,
  slideCount = 1,
  totalCount = 0,
  isProducing = false,
  onValidateTemplateChange = () => true,
  onTemplateChange = () => {},
  onToggleArtboard = () => {},
  onPickTemplate = () => {},
  onEditTemplate = () => {},
  canEditTemplate = false,
  onGenerate = () => {},
  isCheckingImages = false,
  hasUnusableImages = false,
}) => {
  const { t } = useTranslationApp();
  const languageOptions = useMemo(
    () =>
      getContentLanguageOptions({ preset: CONTENT_LANGUAGE_PRESET.TEMPLATES }),
    []
  );
  const language = useWatch({ control, name: "language" });
  const typeOfPlans = useSelectorApp((state) => state.payments?.typeOfPlans);
  const { ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION } = useCreditActionCosts();
  const resolvedCost = resolveActionCostForPlanType(
    ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
    typeOfPlans
  );
  const unitCost = Math.max(0, Number(resolvedCost.amount) || 0);
  const spend = unitCost * Math.max(0, totalCount);
  const languageLabelKey =
    languageOptions.find((item) => item.value === language)?.label || "";
  const languageLabel = languageLabelKey ? t(languageLabelKey) : "";
  const selectedFormats =
    selectedArtboardIDs.length > 0
      ? formatOptions.filter((option) =>
          selectedArtboardIDs.includes(option.value)
        )
      : formatOptions;
  const sizesCount = selectedFormats.length;
  const slidesPerFormat = Math.max(1, Number(slideCount) || 1);
  const imagesPerFormat =
    getProduceProductionRowCount({
      rowCount,
      variationCount,
      hasVariables,
    }) * slidesPerFormat;
  const variationLabel = countLabel({
    t,
    count: variationCount,
    singularKey: TEMPLATES_PRODUCE_SUMMARY_VARIATION_LABEL,
    pluralKey: TEMPLATES_PRODUCE_SUMMARY_VARIATIONS_LABEL,
  });
  const rowsLabel = countLabel({
    t,
    count: rowCount,
    singularKey: TEMPLATES_PRODUCE_ROW_COUNT_SINGLE,
    pluralKey: TEMPLATES_PRODUCE_ROW_COUNT,
  });
  const sizesLabel = countLabel({
    t,
    count: sizesCount,
    singularKey: TEMPLATES_PRODUCE_SUMMARY_SIZE_COUNT,
    pluralKey: TEMPLATES_PRODUCE_SUMMARY_SIZES_COUNT,
  });
  const slidesLabel = countLabel({
    t,
    count: slidesPerFormat,
    singularKey: TEMPLATES_PRODUCE_SUMMARY_SLIDE_COUNT,
    pluralKey: TEMPLATES_PRODUCE_SUMMARY_SLIDES_COUNT,
  });
  const recipe = joinSummaryParts(
    [
      hasVariables ? rowsLabel : null,
      variationLabel,
      sizesCount > 0 ? sizesLabel : null,
      slidesPerFormat > 1 ? slidesLabel : null,
    ],
    SUMMARY_FORMULA_SEPARATOR
  );
  const imagesCountLabel = (count) =>
    countLabel({
      t,
      count,
      singularKey: TEMPLATES_PRODUCE_SUMMARY_IMAGES_COUNT_SINGLE,
      pluralKey: TEMPLATES_PRODUCE_SUMMARY_IMAGES_COUNT,
    });
  const costUnit = getProduceCostUnit({ t, count: spend });
  const rateUnit = getProduceCostUnit({ t, count: unitCost });
  const costLabel = t(TEMPLATES_PRODUCE_SUMMARY_COST, {
    count: spend,
    unit: costUnit,
  });
  const generateLabel = countLabel({
    t,
    count: totalCount,
    singularKey: TEMPLATES_PRODUCE_GENERATE_SINGLE,
    pluralKey: TEMPLATES_PRODUCE_GENERATE,
  });

  return (
    <Box sx={produceCardSx}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography variant="caption" sx={produceKickerSx}>
          {t(TEMPLATES_PRODUCE_CONFIG_KICKER)}
        </Typography>
        <Typography sx={produceCardTitleSx}>
          {t(TEMPLATES_PRODUCE_TEMPLATE)}
        </Typography>
      </Box>

      {!hasProduceableCards ? (
        <Typography variant="body2" color="text.secondary">
          {t(TEMPLATES_PRODUCE_EMPTY_TEMPLATES)}
        </Typography>
      ) : showTemplateSelect ? (
        <Select
          control={control}
          name="templateID"
          label={t(TEMPLATES_PRODUCE_TEMPLATE)}
          variant="filled"
          fullWidth
          size="small"
          rules={optionalRules}
          options={templateOptions}
          validationChange={onValidateTemplateChange}
          actionsChange={onTemplateChange}
          disabled={isProducing}
          MenuProps={templateSelectMenuProps}
          renderValue={(selected) => {
            const option = templateOptions.find(
              (item) => item.value === selected
            );
            return option?.label || "";
          }}
          sx={produceTemplateSelectSx}
        />
      ) : (
        <Box sx={produceSelectedTemplateBoxSx}>
          <Box
            sx={{
              width: 120,
              height: 120,
              flexShrink: 0,
              borderRadius: 1.5,
              overflow: "hidden",
            }}
          >
            <TemplateLayoutPreview card={selectedCard} />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t(TEMPLATES_PRODUCE_SELECTED_TEMPLATE_LABEL)}
            </Typography>
            <Typography sx={{ fontWeight: 600 }} noWrap>
              {templateName}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
              {canEditTemplate ? (
                <Box
                  component="button"
                  type="button"
                  disabled={isProducing}
                  onClick={onEditTemplate}
                  sx={produceChangeButtonSx}
                >
                  {t(BUTTON_EDIT)}
                </Box>
              ) : null}
              <Box
                component="button"
                type="button"
                disabled={isProducing}
                onClick={onPickTemplate}
                sx={produceChangeButtonSx}
              >
                {t(TEMPLATES_PRODUCE_CHANGE)}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {showVariationCount ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={produceFieldLabelSx}>
            {t(
              hasVariables
                ? TEMPLATES_PRODUCE_VARIATIONS_PER_ROW
                : TEMPLATES_PRODUCE_VARIATIONS
            )}
          </Typography>
          <TextField
            control={control}
            name="variationCount"
            variant="filled"
            fullWidth
            size="small"
            rules={optionalRules}
            disabled={isProducing}
            sx={{
              ...produceSelectSx,
              "& .MuiFormHelperText-root": { display: "none" },
            }}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              min: PRODUCE_MIN_VARIATION_COUNT,
              max: PRODUCE_MAX_ROWS,
            }}
            onKeyDown={(event) => {
              if (event.ctrlKey || event.metaKey || event.altKey) return;
              const allowedKeys = [
                "Backspace",
                "Delete",
                "Tab",
                "Escape",
                "Enter",
                "ArrowLeft",
                "ArrowRight",
                "Home",
                "End",
              ];
              if (allowedKeys.includes(event.key)) return;
              if (!/^\d$/.test(event.key)) {
                event.preventDefault();
              }
            }}
            actionsChange={(value) => {
              setValue(
                "variationCount",
                sanitizeProduceVariationCountInput({
                  value,
                  max: PRODUCE_MAX_ROWS,
                })
              );
            }}
            actionsBlur={(event) => {
              setValue(
                "variationCount",
                clampProduceVariationCount({
                  value: event?.target?.value,
                  max: PRODUCE_MAX_ROWS,
                })
              );
            }}
          />
        </Box>
      ) : null}

      {showFormatToggles ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={produceFieldLabelSx}>
            {t(TEMPLATES_PRODUCE_DIMENSIONS)}
          </Typography>
          <ProduceToggleChips
            options={formatOptions}
            selectedValues={selectedArtboardIDs}
            onToggle={onToggleArtboard}
            disabled={isProducing}
          />
        </Box>
      ) : null}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={produceFieldLabelSx}>
          {t(TEMPLATES_PRODUCE_LANGUAGE)}
        </Typography>
        <SelectContentLanguage
          control={control}
          name="language"
          preset={CONTENT_LANGUAGE_PRESET.TEMPLATES}
          persistOnChange
          variant="filled"
          fullWidth
          size="small"
          rules={optionalRules}
          disabled={isProducing}
          sx={produceSelectSx}
        />
      </Box>

      <Box sx={produceSectionDividerSx}>
        <Box sx={produceSummaryBoxSx}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={produceSummaryKickerSx}>
              {t(TEMPLATES_PRODUCE_SUMMARY)}
            </Typography>
            <Typography sx={produceSummaryTotalSx}>
              {imagesCountLabel(totalCount)}
            </Typography>
            {recipe ? (
              <Typography variant="body2" color="text.secondary">
                {recipe}
              </Typography>
            ) : null}
          </Box>

          <Box sx={produceSummaryRowsSx}>
            {selectedFormats.map((format) => (
              <ProduceSummaryRow
                key={format.value}
                label={t(TEMPLATES_PRODUCE_SUMMARY_FORMAT, {
                  size: format.label,
                })}
                value={imagesCountLabel(imagesPerFormat)}
                hint={slidesPerFormat > 1 ? slidesLabel : undefined}
              />
            ))}
            {languageLabel ? (
              <ProduceSummaryRow
                label={t(TEMPLATES_PRODUCE_LANGUAGE)}
                value={languageLabel}
              />
            ) : null}
          </Box>

          {spend > 0 ? (
            <Box sx={produceSummaryCostSx}>
              <ProduceSummaryRow
                label={t(TEMPLATES_PRODUCE_SUMMARY_COST_LABEL)}
                value={costLabel}
                hint={t(TEMPLATES_PRODUCE_SUMMARY_RATE, {
                  count: unitCost,
                  unit: rateUnit,
                })}
              />
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Button
          color="primary"
          fullWidth
          type="button"
          loading={isProducing}
          disabled={
            isProducing ||
            totalCount < 1 ||
            isCheckingImages ||
            hasUnusableImages
          }
          onClick={onGenerate}
          startIcon={
            isProducing ? undefined : (
              <SparklesIcon
                sx={{ color: "#ffffff !important", fontSize: 16 }}
              />
            )
          }
          sx={{ height: 44 }}
        >
          {totalCount < 1 ? t(BUTTON_GENERATE) : generateLabel}
        </Button>
      </Box>
      {isCheckingImages || hasUnusableImages ? (
        <Typography
          variant="caption"
          color={
            hasUnusableImages && !isCheckingImages ? "error" : "text.secondary"
          }
          sx={{ display: "block", textAlign: "center", lineHeight: 1.4 }}
        >
          {t(
            isCheckingImages
              ? TEMPLATES_PRODUCE_IMAGES_PROCESSING
              : TEMPLATES_PRODUCE_IMAGES_UNUSABLE
          )}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ProduceTemplateCard;
